// Pure presence-merge helpers. The live socket reports only CONNECTED players; a
// screen absent from presence keeps whatever the REST list says (last_seen-based
// status). These functions own that merge so the components — and their tests —
// agree on exactly when the live signal wins over the polled one.

import type { ScreenListItem, ScreenStatus } from '@seencast/shared';
import type { InboundMessage, NowPlaying, PresenceScreen } from './ws-messages.ts';

export interface PresenceEntry {
  online: boolean;
  now_playing: NowPlaying | null;
}

/** screen_id → live presence. Only screens the socket has reported appear here. */
export type PresenceMap = ReadonlyMap<string, PresenceEntry>;

export const EMPTY_PRESENCE: PresenceMap = new Map();

/** Replace the whole map from a full `presence` snapshot (connected players). */
export function applyPresenceSnapshot(screens: PresenceScreen[]): PresenceMap {
  const next = new Map<string, PresenceEntry>();
  for (const s of screens) next.set(s.screen_id, { online: s.online, now_playing: s.now_playing });
  return next;
}

/** Apply one `presence_delta` immutably (returns a new map). */
export function applyPresenceDelta(
  prev: PresenceMap,
  delta: { screen_id: string; online: boolean; now_playing: NowPlaying | null },
): PresenceMap {
  const next = new Map(prev);
  next.set(delta.screen_id, { online: delta.online, now_playing: delta.now_playing });
  return next;
}

/** Fold any inbound frame into the presence map (ignores non-presence frames). */
export function reducePresence(prev: PresenceMap, msg: InboundMessage): PresenceMap {
  if (msg.type === 'presence') return applyPresenceSnapshot(msg.screens);
  if (msg.type === 'presence_delta') return applyPresenceDelta(prev, msg);
  return prev;
}

/**
 * The status to render for a screen. A never-paired screen is always
 * "never_paired" (the socket never contradicts that). Otherwise, when the socket
 * has a live entry, it wins (online flips in real time); with no live entry we
 * fall back to the REST-computed status.
 */
export function liveStatusForScreen(screen: ScreenListItem, presence: PresenceMap): ScreenStatus {
  if (screen.status === 'never_paired') return 'never_paired';
  const entry = presence.get(screen.id);
  if (!entry) return screen.status;
  return entry.online ? 'online' : 'offline';
}

/** True when the socket has a fresher signal for this screen than the REST list. */
export function hasLiveSignal(screen: ScreenListItem, presence: PresenceMap): boolean {
  return presence.has(screen.id);
}

/**
 * The "Now playing" label. The socket reports a media_id, usually WITHOUT a name
 * (architect ruling), so resolution is: an explicit name if the hub sent one →
 * the media library name for that media_id (via `resolveMediaName`, e.g. the
 * query cache) → "Unknown media" for an id we can't resolve (deleted / not
 * loaded). A screen that's connected but reporting nothing falls back to the REST
 * last_state playlist name; an offline screen shows nothing (no stale label after
 * a player drops).
 */
export function nowPlayingLabel(
  screen: ScreenListItem,
  presence: PresenceMap,
  resolveMediaName?: (mediaId: string) => string | undefined,
): string | null {
  const entry = presence.get(screen.id);
  if (entry) {
    if (!entry.online) return null;
    const np = entry.now_playing;
    if (np) {
      if (np.name) return np.name;
      return resolveMediaName?.(np.media_id) ?? 'Unknown media';
    }
    // Connected but not reporting a current item — fall through to the REST view.
  }
  return screen.last_state?.current_playlist_name ?? null;
}
